import { useMemo, useRef } from 'react';
import {
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    CalendarToday,
    ChevronLeft,
    ChevronRight,
    Favorite,
    FavoriteBorder,
    LocationOn
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { IEventList } from '../../../interfaces/IEventList';
import { formatFullDateRange, truncateText } from '../../../utils/formatters';
import { EventVisibilityTag } from './EventVisibilityTag';

interface RelatedOrganiserEventsCarouselProps {
    events: IEventList[];
    onToggleFavourite: (eventId: string) => void;
}

const RelatedOrganiserEventsCarousel: React.FC<RelatedOrganiserEventsCarouselProps> = ({
    events,
    onToggleFavourite
}) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const listRef = useRef<HTMLDivElement | null>(null);

    const cardWidth = useMemo(() => (isMobile ? 280 : 340), [isMobile]);

    const scrollCards = (direction: number) => {
        if (!listRef.current) {
            return;
        }

        listRef.current.scrollBy({
            left: direction * (cardWidth + 16),
            behavior: 'smooth'
        });
    };

    const resolveLocationLabel = (event: IEventList): string => {
        const cities = event.locationCity ?? [];
        if (cities.length === 0) {
            return 'Location TBD';
        }

        if (cities.length === 1) {
            return cities[0];
        }

        return `${cities[0]} +${cities.length - 1} more`;
    };

    if (!events.length) {
        return null;
    }

    return (
        <Box sx={{ mt: { xs: 5, md: 8 } }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: '#1f2233'
                        }}
                    >
                        Other Relevant Events By This Organiser
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            mt: 0.5
                        }}
                    >
                        Discover similar experiences from the same organiser.
                    </Typography>
                </Box>

                {!isMobile && events.length > 2 && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                            onClick={() => scrollCards(-1)}
                            sx={{
                                border: `1px solid ${alpha('#493193', 0.25)}`,
                                bgcolor: 'white',
                                '&:hover': {
                                    bgcolor: alpha('#493193', 0.08)
                                }
                            }}
                        >
                            <ChevronLeft />
                        </IconButton>
                        <IconButton
                            onClick={() => scrollCards(1)}
                            sx={{
                                border: `1px solid ${alpha('#493193', 0.25)}`,
                                bgcolor: 'white',
                                '&:hover': {
                                    bgcolor: alpha('#493193', 0.08)
                                }
                            }}
                        >
                            <ChevronRight />
                        </IconButton>
                    </Box>
                )}
            </Box>

            <Box
                ref={listRef}
                sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    pt: 1,
                    mt: -1,
                    pb: 1,
                    '&::-webkit-scrollbar': {
                        height: 8
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha('#493193', 0.3),
                        borderRadius: 99
                    }
                }}
            >
                {events.map((event) => (
                    <Card
                        key={event.id}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: cardWidth,
                            maxWidth: cardWidth,
                            scrollSnapAlign: 'start',
                            borderRadius: 3,
                            overflow: 'hidden',
                            border: `1px solid ${alpha('#493193', 0.12)}`,
                            boxShadow: '0 8px 24px rgba(20, 24, 40, 0.08)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 14px 34px rgba(20, 24, 40, 0.16)'
                            }
                        }}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                height: 170,
                                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            }}
                        >
                            {event.imageUrl && (
                                <Box
                                    component="img"
                                    src={event.imageUrl}
                                    alt={event.title}
                                    title={event.title}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            )}
                            <IconButton
                                onClick={() => onToggleFavourite(event.id)}
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    bgcolor: alpha('#ffffff', 0.92),
                                    '&:hover': {
                                        bgcolor: '#ffffff'
                                    }
                                }}
                            >
                                {event.isFavourite ? (
                                    <Favorite sx={{ color: '#d32f2f' }} />
                                ) : (
                                    <FavoriteBorder />
                                )}
                            </IconButton>
                        </Box>

                        <CardContent
                            sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        lineHeight: 1.25,
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {truncateText(event.title ?? '', 70)}
                                </Typography>
                                <EventVisibilityTag
                                    isInviteOnly={event.isInviteOnly}
                                    isEventPublic={event.isEventPublic}
                                    sx={{
                                        height: 22,
                                        ml: 1,
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <CalendarToday fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {formatFullDateRange(event.startDate.toString(), event.endDate.toString(), event.isSingleDay)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <LocationOn fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {resolveLocationLabel(event)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                                {event.categoryName && (
                                    <Chip
                                        size="small"
                                        label={event.categoryName}
                                        variant="outlined"
                                        sx={{ borderColor: alpha('#493193', 0.28), color: '#493193' }}
                                    />
                                )}
                                {event.subCategoryName && (
                                    <Chip
                                        size="small"
                                        label={event.subCategoryName}
                                        variant="outlined"
                                        sx={{ borderColor: alpha('#2fadd8', 0.35), color: '#1c6a89' }}
                                    />
                                )}
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => navigate(`/events/${event.id}`)}
                                sx={{
                                    mt: 'auto',
                                    borderRadius: '999px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    bgcolor: '#493193',
                                    boxShadow: '0 6px 18px rgba(73, 49, 147, 0.35)',
                                    '&:hover': {
                                        bgcolor: '#3b2778'
                                    }
                                }}
                            >
                                View Event
                            </Button>
                        </CardContent>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};

export default RelatedOrganiserEventsCarousel;
